/**
 * TWO ZONES IN ONE GRID — the candle, and the air around it.
 *
 * <h2>The blockade this exists to break</h2>
 *
 * <p>A chart click resolves to a bar index by its x pixel alone, so every click anywhere in the
 * price grid was one gesture with one meaning. In a replay that is a deadlock: opening a trade
 * puts the manual machine into "awaiting close", which owns the next click — and the bar the
 * reader needs to close on has not been revealed yet, because revealing it needs the very click
 * the trade is holding.
 *
 * <p>So the gesture is split by WHERE in the grid it landed:
 *
 * <pre>
 *   ON a revealed candle — inside its own high-low range   a TRADE action
 *   anywhere else in the grid                              advance the REPLAY
 * </pre>
 *
 * <h2>A masked bar is never a candle</h2>
 *
 * <p>`maskBarsAfter` flattens the hidden future to a horizontal line at the last revealed close,
 * so the region past the playhead is not empty — it is a row of zero-height bars sitting on one
 * price. Without the boundary test, a click within a few pixels of that line would register as a
 * trade action on a bar whose prices the reader has never been shown, which is the leak the whole
 * replay exists to prevent arriving through a mis-click.
 *
 * <h2>And the padding, which is what makes a doji clickable</h2>
 *
 * <p>A bar that opened and closed at the same price with no range is a mathematical line: its
 * high equals its low, so a strict containment test gives it zero clickable height and the reader
 * simply cannot trade it. The tolerance is supplied by the caller as the PRICE equivalent of a
 * few pixels, because only the chart knows its own scale — a tolerance expressed in price here
 * would be most of a 1993 candle and invisible against a 2026 one.
 */

import { BAR_HIGH, BAR_LOW } from './analyticaGeometry';

/** How much slack a candle gets, in SCREEN pixels. The caller converts it to a price. */
export const BAR_HIT_PADDING_PX = 4;

export const HIT_ZONES = Object.freeze({
  /** On a revealed candle: the reader is pointing at a bar and means to trade it. */
  BAR: 'BAR',
  /** Open space, padding, or the masked future: the reader means to move the replay. */
  CANVAS: 'CANVAS',
});

const n = (v) => (Number.isFinite(v) ? v : null);

/**
 * Which zone a click landed in.
 *
 * <p>Defaults to BAR on anything it cannot measure — a missing bar, a missing price, a chart that
 * reported no scale. The split exists to FREE a gesture, not to withhold one, and a reader whose
 * click silently did nothing because a conversion returned undefined is worse off than one whose
 * click did what it has always done.
 *
 * @param {object} input
 * @param {number[]} input.bar       the clicked bar, ECharts order
 * @param {number} input.price       the price the click's y pixel converts to
 * @param {number} [input.tolerance] the price equivalent of `BAR_HIT_PADDING_PX`
 * @param {number} [input.index]     the clicked bar's index
 * @param {number} [input.boundary]  the replay playhead, or null outside a replay
 */
export function hitZoneFor({ bar, price, tolerance = 0, index = null, boundary = null } = {}) {
  // PAST THE PLAYHEAD IS ALWAYS CANVAS, whatever the flattened mask happens to draw there.
  if (Number.isFinite(boundary) && Number.isFinite(index) && index > boundary) {
    return HIT_ZONES.CANVAS;
  }
  const p = n(price);
  const high = n(bar?.[BAR_HIGH]);
  const low = n(bar?.[BAR_LOW]);
  if (p === null || high === null || low === null) return HIT_ZONES.BAR;

  const slack = Math.abs(n(tolerance) ?? 0);
  return (p >= low - slack && p <= high + slack) ? HIT_ZONES.BAR : HIT_ZONES.CANVAS;
}

/** True when the click was on a candle the reader can see. */
export const isBarHit = (input) => hitZoneFor(input) === HIT_ZONES.BAR;

export default hitZoneFor;
